/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    type ReactElement,
    type ReactNode,
    type PropsWithChildren,
    useRef,
    useEffect,
    useCallback,
    useState,
} from 'react';
import { useLocation } from 'react-router';
import { Link } from '@/components/link';
import Search from './search';
import CartBadge from './cart-badge';
import UserActions from './user-actions/user-actions';
import WishlistIcon from './wishlist-icon';
import { useTranslation } from 'react-i18next';
import logo from '/images/logo.svg';
import bgLogo from '/images/bg/bg-wordmark-stacked.svg';
import bgLogoWide from '/images/bg/bg-wordmark.svg';
import { useBrand } from '@/lib/brand/use-brand';
import { HEADER_COMPOSITION } from '@/lib/brand/header-composition';
import SearchPanelTrigger from './search-panel-trigger';
import BgSearchPanel from './bg-search-panel';
import HeaderStoreLink from './header-store-link';
import { HEADER_STORE_LINK_BY_BRAND } from './header-store-link-composition';
import { Button } from '@/components/ui/button';
import { SparklesIcon } from '@/components/icons';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { launchChat } from '@/components/shopper-agent';
import { validateShopperAgentConfig } from '@/components/shopper-agent/shopper-agent.utils';
import { UITarget } from '@/targets/ui-target';
import { Component } from '@/lib/decorators/component';
import { RegionDefinition } from '@/lib/decorators';
import { cn } from '@/lib/utils';
import { useNavContext } from '@/components/navigation-menu-curated/context';

@Component('header', {
    name: 'Header',
    group: 'Layout',
    description: 'Global site header with navigation, search, and cart',
    embedded: true,
    component_id: 'header',
})
@RegionDefinition([{ id: 'announcement', name: 'Announcement' }])
export class HeaderMetadata {}

interface HeaderProps extends PropsWithChildren {
    /**
     * Slot rendered above the header's main rows. Used for announcement banners
     * or other above-the-fold content that should sit at the top of the page.
     */
    announcementSlot?: ReactNode;
    /**
     * Mobile-only slot in the brand row's LEFT corner (hamburger + its overlay
     * drawer — live NM mobile order, COMPARISON.md "Hamburger corner"). When set,
     * it replaces the legacy behavior of rendering a hidden duplicate of
     * `children` there — `children` then renders exactly once (desktop nav
     * row), collapsing the duplicate NavigationMenu instance the raw-catalog
     * nav shipped (see .reference/harvest/ours-desktop-flyout.json caveat).
     */
    mobileMenuSlot?: ReactNode;
    variant?: 'full' | 'checkout';
    /**
     * Does THIS route show the mobile gender tabs? Live NM renders them on the
     * HOMEPAGE ONLY - owner-verified on a real handset across four surfaces on
     * 2026-08-23: home HAS them, search / category / PDP all have none.
     *
     * Deliberately an ALLOW-LIST rather than a suppression list, and default
     * `false`. A suppression list would leave every surface nobody has looked
     * at yet - cart, account, order confirmation - carrying the row by default,
     * so the same defect would be rediscovered one surface at a time. As an
     * allow-list the homepage is the exception it actually is, and a new route
     * is correct without doing anything.
     *
     * Stamped by the app layout from its own route match, exactly like `sticky`
     * and the ticker's `band` - NOT read from `useLocation()` here, because
     * that subscription is deliberately isolated to the Search component (see
     * the wrapper below) so a navigation does not re-render the whole header.
     *
     * DESKTOP IS UNAFFECTED and must stay so: the desktop gender nav is a
     * separate, opposite finding - reported as MISPLACED, not absent.
     */
    mobileGenderTabs?: boolean;
    /**
     * Route-aware stickiness (blast-3: live's SRP wrapper is
     * `header-wrapper--sticky` while the homepage's is not — cross-app drift,
     * matched per page like the ticker band). The app layout stamps the
     * per-route value (same mechanism as the ticker's `band` prop, 973702c);
     * the header reflects it as `data-header-sticky`, and the positioning
     * rule lives in tokens/header.css. Default sticky — the homepage is the
     * live outlier that opts out.
     */
    sticky?: boolean;
}

// Isolates the `useLocation()` subscription so that route changes only re-render the Search component (via key reset)
// without cascading a re-render through the entire `Header` tree, which would unnecessarily re-render the navigation
// menu and other stable children.
function LocationKeyedSearch() {
    const location = useLocation();
    return <Search key={`${location.pathname}${location.search}`} />;
}

/**
 * The header's per-brand composition — breakpoint gates AND structural slots —
 * lives in ONE keyed row in lib/brand/header-composition.ts. The former
 * `HEADER_SWITCH` map that used to sit here was absorbed into it unchanged
 * (2026-08-18), because BG's desktop header is not NM's header re-skinned: its
 * search is a collapsed panel trigger in the LEFT third with no input in the
 * DOM, its account/wishlist/bag are text links rather than glyphs, and it has
 * neither gender tabs nor a My Orders link. Those are structural facts no token
 * can carry, and they have to flip at the same width as the gates — see the
 * evidence block at the top of that file for the capture line citations.
 */

/**
 * The id the search trigger's `aria-controls` points at, and the panel's own
 * `id`. Module-scoped rather than `useId()` because there is exactly one header
 * per document and the value has to be stable across SSR/hydration.
 */
const SEARCH_PANEL_ID = 'header-search-panel';

/**
 * NM header model (live production shape,
 * .reference/harvest/live-nm-desktop-header-anatomy.json):
 *
 *   Desktop (md+):
 *     1. Main row — gender tabs (left) | centered logo | search + visible
 *        submit and the utility cluster (My Orders, Sign In, wishlist, cart)
 *        toward the right. One row: the former separate utility strip and
 *        search rows were consolidated here (COMPARISON.md "Nav row placement
 *        relative to gender toggles/search"). The store control sits at the
 *        top-left of this row, above the gender tabs — see below.
 *
 *        THE STORE CONTROL WAS ABSENT AND IS NOW RENDERED (2026-08-22). Two
 *        successive claims were recorded here, and the history matters because
 *        each was corrected by evidence rather than by opinion.
 *
 *        The FIRST claim, citing COMPARISON.md, was that "Our Stores" and the
 *        "Find a Store" badge are not part of the production header and were
 *        removed with the consolidation. THAT WAS FALSE, and the likely cause
 *        is now known: live injects the control CLIENT-SIDE, so it is absent
 *        from every server-rendered .html save on disk while the surrounding
 *        header markup is present in the same file. A search of only those
 *        files returns a confident zero.
 *
 *        The SECOND claim was that the control had to stay out because live's
 *        is a client-side, geolocation-dependent call — it can be delayed, it
 *        can fail, and it can correctly resolve to no nearby store at all;
 *        there is no Bergdorf Goodman anywhere near Denver. That was TRUE OF
 *        LIVE and true of this build at the time, and it is now spent on both
 *        counts. The storefront resolves real stores against Shopper Stores on
 *        both brands, the no-nearby-store empty state exists and reproduces for
 *        that exact Denver case, and — the part that retires the objection
 *        rather than merely satisfying it — OURS IS NOT A GEOLOCATION CALL AT
 *        ALL. It reads a server-resolved cookie through the root loader, so
 *        there is nothing to delay, nothing to fail and no flash between
 *        states.
 *
 *        Live's evidence, the geometry deliberately NOT copied, the
 *        desktop-only gate and the three-tier proof that BG has NO such
 *        control are all in header-store-link-composition.ts. Read that before
 *        touching this.
 *
 *        THE EXTENSION'S OWN BADGE STAYS DISABLED, and not for the old reason.
 *        src/extensions/store-locator/target-config.json targets
 *        sfcc.header.before.cart with an ICON affordance. Live's control is a
 *        TEXT button at the top-LEFT, so that target is wrong in BOTH position
 *        and shape — and no sfcc.header.before.cart UITarget is mounted in this
 *        header anyway, so flipping the flag alone would render nothing.
 *     2. Nav row  — centered curated category navigation
 *
 *   Mobile (<md): brand row (hamburger LEFT | centered logo | icon rail),
 *   then a full-width search row — recomposed separately against the 375px
 *   live capture; desktop changes are scoped with md+ variants to leave it
 *   untouched.
 *
 *   The main row is a CSS grid (`md:grid-cols-[1fr_auto_1fr]`) so the logo
 *   stays viewport-centered while the right column carries more content
 *   (search + utility cluster) than the left (gender tabs). Exact pixel
 *   values are a phase-3b concern (.reference/harvest/header-3b-skeleton.md)
 *   — only tokens/utilities here, no hand-tuned heights.
 *
 * Phase 2 (design-system styling) applies the *documented* luxe values only:
 * `type-*` roles per element (docs/nm-design-system.md §2), the shared button
 * focus ring (§7.2), and the text field component tokens (§8.11, applied in
 * src/theme/overrides/text-field.css). Anything luxe does not publish stays as
 * it was and is logged as a phase-3 question — pixel-parity tuning against live
 * NM is a later, human-guided pass. Do not add measured widths, negative
 * margins, or breakpoint-specific nudges here.
 *
 * `[--ring:var(--button-focus-border-color)]` is a source-token override (see
 * docs/README-SHAPE-TOKENS.md for the pattern): it repoints the focus ring the
 * shadcn `Button`/`Input` primitives already draw at luxe's documented
 * `#0e7afe`, instead of stacking a second ring on top of them.
 */
export default function Header({
    children,
    announcementSlot,
    mobileMenuSlot,
    variant = 'full',
    sticky = true,
    mobileGenderTabs = false,
}: HeaderProps): ReactElement {
    const { t } = useTranslation('header');
    const headerRef = useRef<HTMLElement>(null);
    // Gender tabs: when a NavContentProvider is present (the app shell), the
    // tabs drive the shared nav-content gender context so the silo tree swaps
    // (live-NM behavior — the toggle previously swapped nothing). Standalone
    // hosts (stories, isolated tests) fall back to purely local state.
    const navContext = useNavContext();
    const [localGender, setLocalGender] = useState<'womens' | 'mens'>('womens');
    const activeGender: 'womens' | 'mens' = navContext
        ? navContext.contextKey === 'men'
            ? 'mens'
            : 'womens'
        : localGender;
    const selectGender = (gender: 'womens' | 'mens') => {
        if (navContext) {
            navContext.setContextKey(gender === 'mens' ? 'men' : 'women');
        } else {
            setLocalGender(gender);
        }
    };
    const config = useConfig();
    const brand = useBrand();
    // Every mobile/desktop gate AND every structural slot below reads from this
    // ONE row — see lib/brand/header-composition.ts.
    const composition = HEADER_COMPOSITION[brand];
    // BG's search is a disclosure: the trigger is in the header bar, the field
    // enters the DOM only once opened (bg-home-d-1280/App.tsx:73-80 has no
    // <input> at all; bg-searchbar-m-375/App.tsx:114 is the opened panel).
    // Dead state on NM, whose search is always mounted.
    const [searchPanelOpen, setSearchPanelOpen] = useState(false);
    const searchPanelRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!searchPanelOpen) return;
        // Opening a disclosure that exists to be typed into should land the
        // caret in it — otherwise the click costs a second click.
        searchPanelRef.current?.querySelector('input')?.focus();
    }, [searchPanelOpen]);
    // Per-brand wordmark. The BG asset is the production BG header wordmark,
    // extracted from legacy bgo-ui's BrandLogo component (see public/images/bg/).
    // BG ships TWO wordmarks and swaps them by width, exactly as live does via its
    // `hide-on-mobile` / `hide-on-desktop hide-on-tablet` span pair. Measured on the
    // owner's live captures:
    //
    //   375 / 440   stacked two-line mark, filling a 108x41 box   (bg-home-m-375:87)
    //   768 / 1024  wide single-line lockup, ~330px               (bg-search-768.png)
    //   1280 / 1440 wide single-line lockup, 398px                (bg-home-d-1280)
    //
    // So the ART steps at `md`, even though BG's header COMPOSITION stays in mobile
    // mode until 1025 — two different breakpoints on one header, and the search
    // PANEL's takeover-to-drawer swap (bg-search-panel.tsx) is a third that happens
    // to share the composition's. Each was measured separately. Do not collapse them.
    //
    // One asset at every width is a regression, not a simplification: the desktop
    // sizing token `--header-logo-art-height: 34px` (brand-bg.css:231) was measured
    // against the WIDE 398x34 lockup, so feeding it the 2.69:1 stacked mark paints a
    // 91px wordmark where live paints 398px. Measured in-browser at 1280 before this
    // fix: 91.46 x 34.
    //
    // NM is unaffected by construction — both slots resolve to the same single asset.
    const logoSrc = brand === 'bg' ? bgLogo : logo;
    const logoSrcWide = brand === 'bg' ? bgLogoWide : logo;
    const logoAlt = brand === 'bg' ? t('logoAltBg', { defaultValue: 'Bergdorf Goodman' }) : t('logoAlt');
    // INTRINSIC ARTWORK DIMENSIONS — the box the browser reserves BEFORE the SVG
    // arrives. Every one of these `<img>`s previously carried neither, and an SVG
    // whose file declares only a `viewBox` (all three of ours do) has no intrinsic
    // size at all until it loads. BG's mobile mark is the sharp case: its CSS is
    // `w-auto h-auto max-w-[108px] max-h-(token)`, i.e. BOTH axes auto, so the
    // pre-load box is literally 0x0 and the whole header — on every page — is
    // shorter than it will be a moment later. That collapse is what Lighthouse's
    // `layout-shifts` audit attributes the deployed cart's largest shift to, and
    // it is the page's only `unsized-images` offender on either brand.
    //
    // WHY THE ROUNDING IS SAFE, and it is not an argument from smallness. The UA
    // stylesheet applies these as the TWO-VALUE form, `aspect-ratio: auto W / H`:
    // the declared ratio governs only while the natural one is unknown, and the
    // asset's own ratio takes over the instant it decodes. So a rounded value is
    // a pre-load placeholder that self-corrects, never a shipped ratio. The
    // absolute numbers never paint either — every code path here pins a height
    // (`md:h-`, `h-` at mobile on NM, `checkoutLogoSize`) or caps the width
    // (`max-w-[108px]`), so the pair only ever supplies a ratio.
    //
    // Read from each asset's own `viewBox`, not from a measurement:
    //   public/images/logo.svg                    viewBox 0 0 588.4  211.9
    //   public/images/bg/bg-wordmark.svg          viewBox 0 0 398    34
    //   public/images/bg/bg-wordmark-stacked.svg  viewBox 0 0 319.68 118.83
    //
    // The pair is per-ASSET, not per-brand, because BG art-directs: the `<source>`
    // carries the wide lockup's 11.71:1 while the `<img>` carries the stacked
    // mark's 2.69:1. A `<source>`'s own width/height override the `<img>`'s for
    // that source, so each element must state the ratio of the artwork IT selects
    // — putting the stacked pair on both would declare a wrong pre-load ratio at
    // md+, where BG's width is `auto` and therefore derived from it.
    const logoArt = brand === 'bg' ? { width: 320, height: 119 } : { width: 588, height: 212 };
    const logoArtWide = brand === 'bg' ? { width: 398, height: 34 } : { width: 588, height: 212 };
    // Logo sizing is BRAND-AGNOSTIC here: one set of utilities, and each brand
    // supplies its own values through the --header-logo-* tokens (brand-nm.css
    // / brand-bg.css, neutral fallbacks in theme/tokens/header.css). The
    // `brand === 'bg' ? 'h-5 md:h-8' : …` ternary this replaced (2026-08-16)
    // was the reason BG rendered a guessed size while its own documented box
    // sat dormant in brand-bg.css. The checkout variant's sibling ternary went
    // the same way (see `checkoutLogoSize` below), so adding a third brand is
    // now a token file, not an edit here.
    //
    // Box model applies as a box model, both brands alike: the Link is the BOX
    // (--header-logo-height plus the vertical pads) and the img is the ARTWORK
    // (--header-logo-art-height). NM 194x76 art in a 194x94 box
    // (les-blast-1-header-1280.md logo row, re-confirmed side-by-side devtools
    // 2026-08-16); BG 398x34 art in a 58px box (docs/bg-design-system.md §9.1).
    //
    // The two art-height tokens are not a breakpoint nicety — the wordmarks
    // have incompatible aspects (NM's script mark ~2.6:1, BG's wide lockup
    // ~11.7:1), so no single sub-md height serves both. Width is constrained at
    // md+ only: NM pins --header-logo-width (194px) and letterboxes via
    // object-contain, while BG leaves it `auto` so its wide lockup stays
    // height-driven on its own aspect.
    // MOBILE IS WIDTH-CONSTRAINED, DESKTOP IS HEIGHT-DRIVEN. A wide wordmark at a
    // fixed height claims its full intrinsic width (BG's is 234px at 20px tall),
    // which on a 390px viewport leaves the icon rail 54px for 140px of icons —
    // they then paint straight over the mark. Capping width and letting height
    // follow is live's own answer: BG's mobile lockup is `max-h-[41px]
    // max-w-[108px]`, constant at 375 and 440 (.reference/captures/bg-home-m-*).
    // The height token becomes a CEILING on mobile rather than a fixed height, so
    // a mark that already fits is untouched.
    // LIVE BG'S 108px MOBILE WIDTH CAP IS APPLIED. The mobile wordmark anchor is
    // `max-h-[41px] max-w-[108px]` at 375 and 440 alike (bg-home-m-375:87,
    // bg-home-m-440:87), and the anchor IS the whole box model because its <img>
    // declares neither height nor width (:89).
    //
    // IT WAS HELD UNTIL NOW, and the reason is worth keeping: that box was
    // measured around a STACKED wordmark (`data-name="bg_stacked"`, viewBox
    // 0 0 319.68 118.83) while this repo shipped only the wide 11.71:1 lockup. A
    // ratio is only valid in the container it was measured in — at 108px wide the
    // wide lockup paints 9.22px tall, against the 135px x 11.53px that
    // `max-w-full` gave it at 375. Both measured in-browser. Applying the cap to
    // the wrong artwork would have traded a right number for a wrong picture.
    //
    // The unblock was an ASSET, not a re-measure: bg-wordmark-stacked.svg now
    // ships, so the 108x41 box lands on the artwork it was measured around. The
    // centre track also stopped being starved once BG's mobile sign-in and heart
    // were removed, which is what made a fixed 108px cap overflow its box here
    // in the first place.
    //
    // WHICH CONSTRAINT BINDS: the width, not the height. The stacked mark is
    // 319.68/118.83 = 2.690:1, so at the full 41px height it wants 110.3px and
    // the 108px cap clips it first, painting ~108 x 40.15. The margin is ~2%, so
    // the picture is right either way — but the binding edge is `max-w`, and a
    // future change to either number should be reasoned about on that basis.
    const brandRowLogoSize =
        'max-h-(--header-logo-art-height-mobile) h-auto max-w-[108px] ' +
        // KNOWN, UNFIXED HERE: the md grid has the same starving problem — an `auto`
        // logo track against `1fr` side tracks that collapse — which puts BG's mark
        // 14px over its icons at 768. `max-w-full` cannot reach it, because 100% of
        // an `auto` track IS the image's max-content width. Fixing it means changing
        // the grid template, which moves both brands' reviewed desktop headers, so it
        // is left flagged rather than guessed at. Pre-existing: identical at 64f30c9.
        //
        // RE-MEASURED 2026-08-19 AND NOT REPRODUCED — the flag is kept, not deleted,
        // because one non-reproducing width does not retire a claim about a grid.
        // BG PDP @768 (`document.documentElement.clientWidth` 768, `data-brand` bg):
        // artwork 187→585, bag button painting 686→738 — 101px clear, not 14px over.
        // @1280 artwork 585→839 against a Sign In link at 1002. So whatever produced
        // the 14px is not present on this surface at these two widths today; BG's md+
        // right group is now a bag glyph plus two TEXT links rather than the icon rail
        // the note was written against. UNVERIFIED at 1024/1440 and on home/search —
        // if it recurs there, this is still where the mechanism is written down.
        // DESKTOP WIDTH CAP, and it is UNCONDITIONAL ON PURPOSE. Live BG caps its
        // wordmark anchor at `max-w-[330px]` from 768 through 1024 and at
        // `max-w-[398px]` from 1280 (bg-search-d-768:88, bg-pdp-d-768, and
        // bg-search-d-1024/bg-home-d-1024 all carry the 330; bg-home-d-1280:76
        // and bg-search-d-1440 carry the 398 — every capture emits the page
        // twice, so each of those is one occurrence). Ours had `md:max-w-none`
        // and let BG's `--header-logo-width: auto` claim the asset's intrinsic
        // 398px at EVERY md+ width, which paints 51.8% of a 768 viewport.
        //
        // NM IS UNMOVED BY ARITHMETIC, not by a gate: NM pins
        // --header-logo-width to 194px, so a 330px ceiling can never bind on it.
        // Verified in-browser rather than argued — NM logo img 194x76 at x=287
        // (clientWidth 768), x=543 (1280) and x=623 (1440), identical before and
        // after. A brand whose mark is wider than 330 would be capped silently,
        // which is why this comment says so out loud.
        //
        // RATIO, NOT A CEILING — the token carries both, and the full measured
        // series plus the two-regime fit live beside it in
        // theme/tokens/brand-bg/header.css. Summary only, so this stays one
        // source of truth: live BG's wordmark ink runs 225 / 308 at 768 / 1024
        // and 293 / 310 / 353 / 398 at 1100 / 1150 / 1280 / 1440 — a flat
        // ~27% of the viewport with the ink HEIGHT scaling in lockstep, which is
        // what makes it a ratio rather than a cap. `max-w-[398px]` is real and
        // binds once, from a viewport of ~1414.5.
        //
        // NM TAKES `none` AND THAT IS ALSO MEASURED, not a default: live NM's
        // ink FALLS 23.0% -> 16.9% across the same four widths and saturates at a
        // fixed 243.5. Same instrument, one flat series and one falling one. NM's
        // width is pinned by --header-logo-width above and unchanged here —
        // verified in-browser at 194x76, x=287 / 543 / 623 for 768 / 1280 / 1440.
        //
        // (The paragraph below is kept for the record of what this replaced.)
        // A CEILING IS NOT LIVE'S MECHANISM, AND THE FIRST PASS SHIPPED THE
        // CEILING ONLY.
        // Measured 2026-08-21 across every live BG width on disk (colour-managed
        // canvas reads of .reference/live-shots/*, page strip located from each
        // shot's own white header band, DPR 2 confirmed per file), live BG's
        // wordmark INK is:
        //
        //     768 -> 225.0   1024 -> 308.0   (mobile-composition regime)
        //    1100 -> 293.0   1150 -> 310.0   1280 -> 353.0   1440 -> 398.0
        //
        // — i.e. 26.6% to 30.1% of the viewport at every one of them, with the
        // ink HEIGHT scaling in lockstep (24.5, 26.5, 29.5, 33.5 at the four
        // desktop widths, holding the asset's 11.7:1 aspect). That is a RATIO,
        // not a cap: the 330 and 398 ceilings above never bind at any width
        // measured, and 398 is simply where the ratio ARRIVES at the asset's
        // intrinsic width — at 1440, not at 1280 where we paint it today.
        //
        // That pass shipped the ceiling alone and said so; the ratio landed
        // afterwards, once the six-width series and the NM control were in hand.
        //
        // THE SAME READ SAYS NM DOES NOT DO THIS, which is why nothing about it
        // may be generalised to both brands: live NM's wordmark ink measures
        // 176.5 / 213.0 / 243.5 / 243.5 at 768 / 1024 / 1280 / 1440 — a FALLING
        // 23.0% -> 20.8% -> 19.0% -> 16.9%, saturating at a fixed 243.5 once its
        // container stops growing. Same instrument, same four widths, one flat
        // series and one falling one.
        //
        // The `xl:max-w-[398px]` arm that guarded BG's desktop against the 330
        // ceiling is retired with the ceiling: the token now steps at 1025, BG's
        // own boundary, so no Tailwind scale key is involved at all.
        'md:h-(--header-logo-art-height) md:max-h-none md:max-w-(--header-logo-max-width) ' +
        'md:w-(--header-logo-width) md:object-contain';
    const brandRowLogoBox =
        'md:h-(--header-logo-height) md:pt-(--header-logo-pad-top) md:pb-(--header-logo-pad-bottom)';
    // The checkout variant's wordmark, same treatment one breakpoint later: the
    // stripped checkout bar steps at `lg`, not `md`, and has no box model to
    // size (see the token comment in theme/tokens/header.css). This was the last
    // brand ternary in the file — `brand === 'bg' ? 'h-5 lg:h-7' : 'h-8 lg:h-10'`
    // — retired 2026-08-16 with today's rendered sizes carried into the overlays
    // unchanged.
    const checkoutLogoSize = 'h-(--header-checkout-logo-art-height-mobile) lg:h-(--header-checkout-logo-art-height)';
    const showChat =
        variant === 'full' &&
        (config.commerceAgent?.enabled === 'true' || config.commerceAgent?.enabled === true) &&
        validateShopperAgentConfig(config.commerceAgent);
    const updateHeaderHeight = useCallback(() => {
        if (headerRef.current) {
            const height = `${headerRef.current.offsetHeight}px`;
            headerRef.current.style.setProperty('--header-height', height);
            document.documentElement.style.setProperty('--header-height', height);
        }
    }, []);

    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        updateHeaderHeight();
        const observer = new ResizeObserver(updateHeaderHeight);
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateHeaderHeight]);

    if (variant === 'checkout') {
        // Minimal checkout chrome: logo only, no cart icon — deliberate NM pattern
        // (checkout renders its own order summary; the flyout is a redundant exit
        // path). POC decision 2026-08-15.
        return (
            <header
                ref={headerRef}
                data-header-sticky="true"
                className="bg-header-background text-header-foreground z-50 [--ring:var(--button-focus-border-color)]">
                <div className="section-container">
                    <div className="flex items-center h-16">
                        <Link to="/" className="flex-shrink-0 flex items-center" data-testid="header-logo">
                            {/* The WIDE lockup at every width — checkout never takes the
                                stacked mark. Both checkout height tokens are 11.71:1 boxes
                                (brand-bg.css:454-455, verified 234.11x20 sub-lg and
                                327.75x28 at 1280), which is the wide lockup's ratio.
                                Regression guard only: checkout is deliberately unworked —
                                live BG's is bot-blocked from capture, so there is nothing
                                to build against. Whether this header should exist at all
                                is an open question, not a task. */}
                            <img
                                src={logoSrcWide}
                                alt={logoAlt}
                                width={logoArtWide.width}
                                height={logoArtWide.height}
                                className={cn('w-auto [filter:var(--header-logo-filter)]', checkoutLogoSize)}
                            />
                        </Link>
                    </div>
                </div>
            </header>
        );
    }

    // Base role: `labelLargeUppercase` (§2.2), element metrics via the
    // --gender-tab-* component tokens (tokens/header.css). Corrected from
    // li-inherited 25px to the button's computed 16px (owner-verified query
    // 2026-08-16): 16px / 400 / uppercase / ls normal on the existing sans
    // stack; the 52px MEN gap and 5px active underline stand. BG pins its own
    // values in brand-bg.css. `focus-ring-button` supplies §7.2's shared focus
    // ring — this is a raw <button>, so it had no focus-visible affordance at
    // all. The active underline and hover fade are phase-1 structure: luxe
    // publishes no state matrix for a text-only tab (§10 open question 5).
    //
    // `idSuffix` exists ONLY so the mobile row below can mount a SECOND copy
    // without duplicating a data-testid. Every existing selector — unit tests,
    // the e2e page object, the token specs — resolves `header-gender-tab-womens`
    // and `header-gender-nav` to exactly one node, and a duplicate would make
    // `getByTestId` throw rather than fail informatively. The desktop instance
    // therefore keeps the bare ids (suffix ''), and the mobile instance takes
    // `-mobile`. See the mobile row's own comment for why two instances rather
    // than one repositioned element.
    const genderTab = (gender: 'womens' | 'mens', label: string, idSuffix = '') => (
        <button
            onClick={() => selectGender(gender)}
            aria-pressed={activeGender === gender}
            data-testid={`header-gender-tab-${gender}${idSuffix}`}
            className={cn(
                'type-label-large text-(length:--gender-tab-font-size) tracking-(--gender-tab-letter-spacing)',
                // Padding-bottom is live's own `pb-[6px]` on NM (see
                // --gender-tab-pad-bottom in tokens/brand-nm/header.css for the
                // ink-to-ink derivation). Written as an arbitrary property with
                // a `0.25rem` fallback rather than added to the shared neutral
                // layer: 0.25rem IS the `pb-1` this replaces, so an unset brand
                // renders byte-identically and no shared claim is made.
                'uppercase cursor-pointer [padding-bottom:var(--gender-tab-pad-bottom,0.25rem)] border-b-(length:--gender-tab-underline) focus-ring-button',
                activeGender === gender ? 'border-header-foreground' : 'border-transparent hover:opacity-50'
            )}>
            {label}
        </button>
    );

    /*
        Extracted to a variable ONLY so the store-control branch below can place
        the very same element in two different parents without duplicating it.
        Its markup and class string are unchanged, which is what keeps a brand
        without a store control rendering byte-identically to before.
    */
    const genderNavWith = (idSuffix: string, showClass: string) => (
        <nav
            aria-label={t('genderNavLabel', { defaultValue: 'Shop by category' })}
            data-testid={`header-gender-nav${idSuffix}`}
            className={cn('items-center gap-(--gender-tab-gap)', showClass)}>
            {genderTab('womens', t('genderWomen', { defaultValue: 'Women' }), idSuffix)}
            {genderTab('mens', t('genderMen', { defaultValue: 'Men' }), idSuffix)}
        </nav>
    );

    // The DESKTOP instance, unchanged: same testid, same class string, same two
    // mount sites below. Routing it through the factory does not alter a byte of
    // its markup, which is what keeps the desktop arm and its e2e certification
    // out of scope for the mobile reveal.
    const genderNav = genderNavWith('', composition.desktopFlex);

    return (
        <header
            ref={headerRef}
            data-header-sticky={sticky ? 'true' : 'false'}
            data-header-breakpoint={composition.breakpoint}
            // The header is position:relative via overrides/navigation.css (the
            // curated-nav flyout anchors absolute to its bottom edge; owner,
            // 2026-08-16: the flyout must always render below the cat nav,
            // never cover it). The rule lives in unlayered CSS because a
            // `relative` utility here was defeated in this element's cascade
            // (verified: class present, computed static) — same
            // unlayered-beats-utilities family as the header svg color.
            //
            // BOTTOM HAIRLINE, NM ONLY. Live NM hangs its rule off the BOTTOM
            // OF THE WHOLE STICKY ASSEMBLY — BELOW the search row, not between
            // the brand row and it. Our `<header>` is that same assembly —
            // brand row + search row, closing at y=186 — so the border belongs
            // on this element and NOT on --header-main-border-*, which is
            // scoped to the inner brand row (a different box, and the reason
            // that token stays 0 here).
            //
            // RE-SOURCED 2026-08-19, and it survived intact. The original
            // citation was the barred nm-search-m-440 dump. Both halves of the
            // claim are now read straight off the owner's own pixels —
            // `.reference/live-shots/nm-search-390.png`, converted to sRGB
            // (the PNGs carry an iCCP profile; raw samples read ~3 levels low),
            // scale 2.0 px/CSS px, page origin x=602/y=238:
            //
            //   page y 156.0 and 156.5   rgb(225,225,225), all 780 px wide
            //   page y 155.5 and 157.0   rgb(255,255,255), all 780 px wide
            //
            // A full-bleed 1px rule at y=156→157 and NOTHING above it, so the
            // POSITION is confirmed (nothing paints between the brand row and
            // the search row) and the COLOUR is confirmed at exactly
            // rgb(225,225,225). Decoder calibrated per .reference/live-shots/
            // INDEX.md: nm-pdp-1280.png's hero ground reads 237 through the
            // same pipeline. The assembly boundary is independently corroborated
            // by the owner's DevTools readout in L1-nm-search-390.md, where
            // `div.sticky-header.active` measures 157 tall with the 42px search
            // row inside it.
            //
            // Live BG also carries one, and it is MEASURED — this comment used
            // to say "unmeasured, not merely deferred" and that was a false
            // negative produced by searching the wrong channel. The value is
            // not in a className; the decompiler emits it as an inline style:
            //   <div className="border-b box-content flex justify-center …"
            //        style={{"borderBottomColor":"rgb(225, 225, 235)"}}>
            // That exact pair is in 33 of 33 BG captures, none of them dumps
            // (bg-home-d-1280:117 and :1051 are the page's two emissions of the
            // same node). tokens/brand-bg.css already carries the value on
            // --header-main-border-color with the same derivation.
            // Channels searched: className (miss — this is why the audit read
            // zero) and style={{…}} (hit, every BG capture).
            //
            // COLOUR IS THE SHARED --header-border, NOT live's 225,225,225.
            // The exact per-brand neutrals need brand-nm/header.css and
            // brand-bg.css, which this change does not own; --header-border
            // (rgba(0,0,0,.08) ≈ rgb(235,235,235) on white) is the nearest
            // existing token. The ~10-level delta is reported, not silently
            // kept — and it is now a delta against a MEASURED 225, not an
            // inferred one.
            className={cn(
                'bg-header-background text-header-foreground z-50 [--ring:var(--button-focus-border-color)]',
                // THIS ELEMENT CARRIES THE ASSEMBLY'S ONLY HAIRLINE, and that is
                // live's own structure: live puts a single
                // `border-bottom: 1px solid rgb(225,225,225)` on `.header-wrapper`
                // — the box that WRAPS both the header row and the cat nav — so
                // the line paints BELOW the nav rather than inside it.
                //
                // We were painting two. Measured at clientWidth 1280 by rendering
                // live's own signed-out page save with JS disabled
                // (.reference/live-shots/owner-2026-08-22/designers-nm-1280.html)
                // and scanning every bordered element in both header subtrees:
                // ours had a 1px border-bottom at y=214 (the nav row's own) AND
                // one at y=215 (this element), 1px apart. Live had exactly one,
                // at the assembly's bottom edge. That is the owner's "our header
                // border is double thick, possibly bottom on one element top on
                // another, but lives looks thinner" — it is bottom-on-two.
                //
                // THE NAV ROW'S BORDER IS NOT REMOVED, IT IS MADE TRANSPARENT
                // (--header-nav-border-color, brand-nm/header.css). Removing the
                // width would reflow: `h-(--header-nav-height)` is a border-box
                // 44px, so dropping the 1px would hand it back to content and
                // move every cat-nav link. Transparency kills the second line
                // and moves nothing.
                //
                // COLOUR VIA --header-assembly-border-color, not by editing the
                // shared --header-border: that token is the fallback here and is
                // read elsewhere, so retargeting it would be a shared claim made
                // from one brand's measurement. NM declares the measured 225;
                // any brand that declares nothing keeps today's paint exactly.
                //
                // SUB-MD IS UNCHANGED and still needs this line — there is no nav
                // row below md, so this element is the only thing that can close
                // the assembly, and the 95px arithmetic in the padding comment
                // below counts this 1px explicitly.
                brand === 'nm' &&
                    'border-b [border-bottom-color:var(--header-assembly-border-color,var(--header-border))]',
                // ASSEMBLY PADDING, sub-md. Live's sticky-header closes at y=157
                // with `pb-[9px]` above its hairline; the comment above already
                // recorded that close and that ours ran to 186. This is what
                // closes that gap: brand row 45 + search row 42 + 7 + 1px border
                // = the 95px live's header occupies below its ticker.
                // Fallback 0px = today's rendering for any brand that does not
                // set it; md+ resets because that composition has a nav row.
                '[padding-bottom:var(--header-pad-bottom-mobile,0px)] md:[padding-bottom:0px]'
            )}>
            {announcementSlot}

            {/* Zone — [mobile hamburger | gender nav] | top-anchored centered logo |
                [desktop right-column stack].
                Mobile (<md) keeps the finished recomposition untouched: a flex row
                whose flex-1 side columns center the logo (hamburger LEFT x=11, logo
                centered, sign-in/wishlist/cart right — COMPARISON.md "Hamburger
                corner"); the hamburger breakpoint is per-brand: BG keeps it through
                1024 and loses it at 1025, because live BG still renders the
                hamburger at 1024 on three surfaces
                (.reference/captures/bg-home-d-1024, bg-search-d-1024, bg-pdp-d-1024)
                and renders the full desktop header at the next pixel (the owner's
                .reference/live-shots/owner-bg-plp-boundary-{1,2,3}.png) — see
                HeaderComposition.breakpoint, and BG_DESKTOP_MIN_WIDTH_PX for why the
                legacy 1025px SCSS figure is STILL not the evidence even now that it
                names the right number.
                Desktop (md+) switches the SAME row to a 1fr/auto/1fr grid whose
                equal side tracks keep the logo viewport-centered — but the zone is
                LAYERED, not centerlined (live-nm-desktop-flyout-clothing.jpeg +
                live-nm-desktop-header-anatomy.json + the owner's 2026-08-16 devtools
                measurements). In-zone bands at 1440:
                  - logo: anchored to the zone TOP, 94px tall, overhanging the
                    90px zone by ~4px — its upper half has its own airspace;
                  - right column (right-edge aligned, top→bottom): My Orders
                    small link (y≈10-27), icon row Sign In/wishlist/cart
                    (y≈48-68), search input+submit (y≈83-110 — the search bottom
                    intentionally runs past the zone edge into the 30px gap via
                    plain flow overflow, exactly as live's does; the zone keeps
                    default visible overflow and no child uses negative margins);
                  - gender tabs: anchored to the zone's BOTTOM band on the left,
                    level with the search line.
                md:items-stretch replaces the old single-centerline items-center so
                each column controls its own vertical anchor, and grid-rows pins
                the single track to the 90px zone — without it the auto track
                grows to the right column's overflowing content and the
                bottom-anchored gender tabs would key off the wrong edge. */}
            {/* 3b: the header frames itself (--header-gutter, not .section-container's
                page padding); the zone takes the live 90px height at md+ and is
                followed by the live 30px gap before the nav row (owner, 2026-08-16:
                total chrome 214 = 50 ticker + 90 zone + 30 gap + 44 nav). */}
            {/* 3b-bis: the main row is a BAND, not just a row. Live BG's whole
                mobile header IS this one band — `border-b ... h-[69px]` with
                the row inside carrying `pt-0 pr-3 pb-[14px] pl-[15px]`
                (bg-home-m-375/App.tsx:71-72, byte-identical at 440) — so the
                band needs a sub-md height, a hairline and an inset of its own.
                All three are tokens whose NEUTRAL values are what NM renders
                today (auto / 0 / the `.section-container` px ladder), which is
                why NM does not move; the `md:px-(--header-gutter)` this
                replaced is now the pads' own md+ step in tokens/header.css.
                `md:h-auto` hands the height back to the row's
                md:h-(--header-main-height) at desktop. */}
            <div
                className={cn(
                    'section-container md:mb-(--header-zone-gap)',
                    // FRAME CAP IS ITS OWN TOKEN, and the neutral RESTATES
                    // `.section-container`'s own value so an unset brand is a
                    // no-op by construction rather than by argument.
                    // `.section-container` caps at `max-w-screen-2xl` = 1536px
                    // (read as `maxWidth: "1536px"` off the running band);
                    // 96rem is that number at the 16px root this app serves.
                    // Live BG's header row is `max-w-[1440px]` — see the
                    // "Desktop header frame" block in
                    // theme/tokens/brand-bg/header.css for the eleven emissions
                    // it is read from and for what the 96px difference costs at
                    // 1500/1600/1920. Written out in full, never composed: the
                    // Tailwind scanner reads source text, so a class built from
                    // a constant lands on the element and emits no rule.
                    '[max-width:var(--header-band-max-width,96rem)]',
                    'h-(--header-main-height-mobile) md:h-auto',
                    'pt-(--header-band-pad-top) pr-(--header-band-pad-right)',
                    'pb-(--header-band-pad-bottom) pl-(--header-band-pad-left)',
                    'border-b-(length:--header-main-border-width) [border-bottom-color:var(--header-main-border-color)]'
                )}>
                <div
                    // `h-full` lets the row fill the band so `items-center` has a
                    // band to centre in (live BG's band is `flex` and its row
                    // stretches — bg-home-m-375:71-72). Inert wherever the band
                    // height is `auto`, which is every NM width.
                    className={cn(
                        'flex items-center gap-x-2 h-full',
                        // Per-brand MOBILE track model — lib/brand/header-composition.ts,
                        // `mobileRowTracks`. NM takes equal `1fr` side tracks around an
                        // `auto` wordmark so the mark centres on the VIEWPORT; under the
                        // plain `flex` below it centred on the space between two
                        // different-width groups instead, putting it 52px left at 375,
                        // 390 and 440 alike ((36-140)/2, measured). Live NM has the same
                        // imbalance and corrects it to a -0.5px offset with its own
                        // `.shift-right` translate. BG's value is the empty string, so
                        // `flex` survives tailwind-merge and BG's row is byte-identical.
                        composition.mobileRowTracks,
                        'md:grid md:grid-cols-[1fr_auto_1fr] md:grid-rows-(--header-main-height) md:items-stretch md:h-(--header-main-height)'
                    )}
                    data-testid="header-main-row">
                    {/* 3c blast-1: the tabs anchor via --gender-tab-bottom-inset (NM 0 —
                        flush to the zone bottom, level with the y≈88 search line the
                        icon-row offset produces; live's tab content actually overflows
                        past the zone, blast "left section top:43.2"). Tab gap is the
                        live 52px MEN margin-left via --gender-tab-gap. */}
                    <div className={composition.leftZone}>
                        {/* Live BG hangs the hamburger off the group's top-left on
                            `mt-[5px] mr-[5px]` (bg-home-m-375:74, same at 440).
                            Neutral 0/0 is NM's current rendering. */}
                        <div
                            className={cn(
                                composition.mobileOnly,
                                'mt-(--header-hamburger-margin-top) mr-(--header-hamburger-margin-right)'
                            )}
                            data-testid="header-mobile-menu">
                            {mobileMenuSlot ?? children}
                        </div>
                        {/* THE STORE CONTROL IS BACK, and the reason it was absent
                            is spent rather than forgotten. The block below used to
                            explain that live NM's store link is a client-side,
                            geolocation-dependent call with no service behind it in
                            this build. That was true and is no longer: ours reads a
                            server-resolved cookie via the root loader, so there is
                            no geolocation, no client lookup and no flash between
                            states. Live's evidence, ours-versus-live geometry, the
                            desktop-only gate and the proof that BG has NO such
                            control are all in header-store-link-composition.ts.

                            It stacks ABOVE the tabs, which is where live puts it —
                            first element of the header's left region, mirroring My
                            Orders on the right. The column wrapper exists only for
                            brands that HAVE the control; every other brand keeps
                            the bare nav it rendered before, so BG's left zone is
                            byte-identical. The wrapper is bottom-aligned by the
                            zone's own md:items-end, so adding a line above the tabs
                            does not move the tabs. */}
                        {composition.genderTabs &&
                            (HEADER_STORE_LINK_BY_BRAND[brand] ? (
                                <div className="hidden md:flex md:flex-col md:items-start">
                                    <HeaderStoreLink />
                                    {genderNav}
                                </div>
                            ) : (
                                genderNav
                            ))}
                        {/* BG's desktop search slot. Same testid as NM's right-column
                            slot because it IS the desktop search affordance — only its
                            zone and its shape differ (bg-home-d-1280/App.tsx:73-80: left
                            third, `role="button" aria-label="Open search panel"`, icon +
                            the word "Search", no input in the DOM). Rendering it here
                            rather than in the right column is the WRONG CONTAINER half
                            of structure-audit.md §1c's finding; the trigger shape is the
                            WRONG SHAPE half. */}
                        {/* UNGATED ON PURPOSE. The trigger does not "appear at
                            desktop" on BG — it is present at EVERY captured
                            width: bg-home-m-375:80, bg-home-m-440:80,
                            bg-pdp-d-768:80, bg-home-d-1024:80 and
                            bg-home-d-1280:75 all carry the same
                            `aria-label="Open search panel"` control in the left
                            group beside the hamburger. Only its shape steps
                            (icon-only at mobile, icon + the word "Search" from
                            1280). The `hidden lg:block` this replaced was the
                            desktop half of a false premise — that BG's mobile
                            search is NM's full-width field row — and the mobile
                            half of it (the row below) is gone with it. The whole
                            block is already BG-only via `desktopSearch`. */}
                        {composition.desktopSearch === 'left-panel-trigger' && (
                            <div data-testid="header-search-desktop">
                                <SearchPanelTrigger
                                    open={searchPanelOpen}
                                    onToggle={() => setSearchPanelOpen((wasOpen) => !wasOpen)}
                                    panelId={SEARCH_PANEL_ID}
                                    // The control is ungated; its WORD is not.
                                    // Live BG's mobile trigger is icon-only and
                                    // the "Search" span first appears at 1280.
                                    labelGate={composition.desktopInline}
                                />
                            </div>
                        )}
                    </div>

                    <Link
                        to="/"
                        className={cn(
                            // The logo is what gives way when space runs out — it is the
                            // only element here whose size is negotiable. The icon rail and
                            // the menu button are fixed affordances.
                            // Block padding is a token so the mobile brand row can
                            // take live NM's authored `h-[45px]`. The FALLBACK is
                            // `0.5rem` — exactly the `py-2` this replaced — so every
                            // brand that does not set the token renders byte-identically,
                            // BG included. NM sets 0 (brand-nm/header.css): its 39px
                            // wordmark plus 16px of padding could not fit a 45px row.
                            // Arbitrary-property syntax rather than `py-(--token)`
                            // because Tailwind's shorthand has no fallback form, and the
                            // neutral default must live here — theme/tokens/header.css is
                            // not this change's to write.
                            'min-w-0 flex-1 flex items-center justify-center',
                            '[padding-block:var(--header-logo-pad-block-mobile,0.5rem)]',
                            // Live BG's wordmark anchor carries `mt-[14px]` at
                            // 375/440 (bg-home-m-375:87). Neutral 0 = NM today;
                            // BG steps it back to 0 at `md`, where the <img>'s
                            // own `md:` resets hand it the desktop box.
                            'mt-(--header-logo-margin-top-mobile)',
                            // `md:[padding-block:0px]` and not `md:py-0`: the base is now an
                            // arbitrary PROPERTY, and Tailwind gives no ordering guarantee
                            // between that and a shorthand utility, so the md+ reset has to
                            // be the same shape to reliably win. md+ box comes from
                            // brandRowLogoBox.
                            // RESERVE THE LANE THE RIGHT GROUP PAINTS INTO.
                            //
                            // The right zone below carries `relative
                            // right-(--header-right-zone-inset)` — live BG's own
                            // mechanism, on the element, at all five captured widths
                            // (bg-home-m-375:96, bg-home-d-1280:92). `right` is a PAINT
                            // offset: it moves the group's pixels left by the inset
                            // while leaving its layout box where the flex row put it.
                            // The wordmark is `flex-1`, so it grows to fill every pixel
                            // up to that layout box — including the strip the bag glyph
                            // now paints in. Measured on our app @390, brand `bg`,
                            // before this line: logo link 80→318, artwork 82→316, bag
                            // button layout 326→378 painting at 308→360. The glyph
                            // covered the last 8px of GOODMAN's N.
                            //
                            // Padding-right of the same inset takes those pixels out of
                            // the link's CONTENT box, so `max-w-full` on the <img> can no
                            // longer reach them. It is the narrowest fix that touches
                            // only the element that overgrew: the right zone's box, the
                            // row's tracks and the paint offset are all unchanged.
                            //
                            // Compensation retired: the stacked mark (bg-wordmark-stacked.svg,
                            // max-w-[108px]) no longer bleeds into the right zone.
                            // Live's own mechanism needs no lane reserve because the stacked
                            // mark is capped at 108px in a w-[110px] track (bg-home-m-375:85-88).
                            'md:pr-0',
                            'md:flex-none md:justify-start md:self-start md:[padding-block:0px]',
                            brandRowLogoBox
                        )}
                        data-testid="header-logo">
                        {/* `display: contents` — the <picture> must not introduce a box
                            of its own, or it becomes the flex/grid child instead of the
                            <img> and the logo track's sizing stops reaching the artwork.
                            The <source> is emitted only when the two assets differ, so
                            NM renders a bare <img> exactly as before. */}
                        <picture className="contents">
                            {logoSrcWide !== logoSrc && (
                                <source
                                    media="(min-width: 768px)"
                                    srcSet={logoSrcWide}
                                    width={logoArtWide.width}
                                    height={logoArtWide.height}
                                />
                            )}
                            <img
                                src={logoSrc}
                                alt={logoAlt}
                                width={logoArt.width}
                                height={logoArt.height}
                                className={cn(
                                    'w-auto [filter:var(--header-logo-filter)]',
                                    brandRowLogoSize,
                                    // Per-brand MOBILE art sizing — see
                                    // `mobileLogoArt`. It trails `brandRowLogoSize`
                                    // so tailwind-merge resolves the `h-` and
                                    // `max-h-` pairs in its favour; empty on BG, so
                                    // BG's class string is unchanged.
                                    composition.mobileLogoArt
                                )}
                            />
                        </picture>
                    </Link>

                    {/* Right column: a plain flex row on mobile (icon rail), a
                        top-aligned right-edge stack at md+. DOM order mirrors the
                        visual layers (My Orders / icon row / search) so the stagger
                        is pure flow — owner spec 2026-08-16: 24px visual whitespace
                        below the ticker (--header-utility-top-inset), 8px to the
                        icon row, 20px to the search bar (per-row margin tokens;
                        the gaps differ so no shared flex gap). */}
                    {/* CORRECTED 2026-08-21. This comment used to read "the right
                        group is `position:relative` with `right:18px`, on the
                        element, at all five captured widths". The `right:18px` is
                        real; the `position:relative` is not, and without it the
                        property paints nothing. Every BG capture, with a positive
                        control in the same command (`relative` occurs 110-226
                        times per file, so the search reaches the content):

                          bg-home-m-375   grow right-[18px] basis-0 pt-3 pr-0 pb-3 pl-3
                          bg-home-m-440   grow right-[18px] basis-0 pt-3 pr-0 pb-3 pl-3
                          bg-search-m-375 grow right-[18px] basis-0 pt-3 pr-0 pb-3 pl-3
                          bg-search-d-768 grow right-[18px] basis-0 pt-3 pr-0 pb-3 pl-3
                          bg-home-d-1024  w-[33.3333%] right-[18px] pt-3 pr-0 pb-3 pl-3 shrink-[0]
                          bg-home-d-1280  w-[33.3333%] right-[18px] pt-3 pr-0 pb-3 pl-3 shrink-[0]
                          bg-search-d-1440 w-[33.3333%] right-[18px] pt-3 pr-0 pb-3 pl-3 shrink-[0]

                        Not one declares `relative`, `absolute` or `fixed`. The
                        decompiler emits COMPUTED `right`, which a static element
                        resolves and ignores — so we reproduced a live property
                        that has no live effect, on a `relative` element where it
                        does, and walked BG's mobile bag 18px inboard.

                        The offset is now GATED rather than deleted — see
                        `rightZoneOffset` in lib/brand/header-composition.ts for
                        the band it still applies over and why the desktop half
                        was left alone. It is still a paint offset, not a margin,
                        so it must not change the zone's layout box; the
                        `relative` stays unconditional and is safe here, because
                        every absolutely-positioned descendant in this zone (the
                        cart badge count, the search field's icon) already anchors
                        to its OWN relative wrapper, so no containing block moves. */}
                    <div className={cn(composition.rightZone, 'relative', composition.rightZoneOffset)}>
                        {/* My Orders keeps the `labelSmallDefault` role it carried
                            in the retired utility strip (§2.2 — 12px comes from the
                            role) plus the element-scoped 0.5px tracking (blast-1).
                            The earlier mr-30 was live box-model noise (dropped, owner,
                            2026-08-16): the real spec is the RELATION — the text's
                            right edge sits ON the 30px frame line, i.e. flush right
                            with the column, same line as the search box's right
                            edge. `focus-ring-button` supplies §7.2's shared focus
                            ring for the raw link.
                            BG has no My Orders link anywhere in its header bar
                            (bg-home-d-1280/App.tsx:92-107 is Sign in / Wishlist /
                            Bag and nothing else; `grep -ani "my orders"` over the
                            whole capture returns no hits) — structure-audit.md §1c
                            EXTRA. The gate is `composition.myOrders`, so the link's
                            own `hidden md:inline-block` is untouched for NM. */}
                        {composition.myOrders && (
                            <Link
                                to="/account/orders"
                                data-testid="header-my-orders"
                                className="hidden md:inline-block type-label-small tracking-(--header-my-orders-letter-spacing) whitespace-nowrap underline focus-ring-button hover:opacity-50 transition-opacity">
                                {t('myOrders', { defaultValue: 'My Orders' })}
                            </Link>
                        )}
                        {/* Icons match live rects; framework Button padding
                            stripped (owner, 2026-08-16) — live's 28px side paddings
                            exist BETWEEN icons, so the spacing is this row's gap
                            token, never padding inside the buttons. Vertical
                            placement comes from the column's shared even gap
                            (no per-row offset). */}
                        <div className={composition.utilityRow} data-testid="header-icon-row">
                            {showChat && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="cursor-pointer text-header-foreground hover:bg-transparent hover:opacity-50 transition-opacity"
                                    onClick={() => launchChat()}
                                    aria-label={t('openChat')}>
                                    <SparklesIcon />
                                </Button>
                            )}
                            <UserActions />
                            <WishlistIcon />
                            <CartBadge />
                        </div>
                        {/* Desktop search is the right column's bottom layer (live
                            NM: persistent visible input + submit); the mobile
                            full-width search row below is untouched. 3c blast-1
                            (les-blast-1-header-1280.md "search container"): 512
                            wide at 1280 — --header-search-width; the input/submit
                            widths land inside search.tsx.
                            Brands whose search is a left-third panel trigger (BG)
                            render their slot in the LEFT zone above instead — the
                            two arms are mutually exclusive, so exactly one element
                            carries `header-search-desktop` at any time. */}
                        {composition.desktopSearch === 'right-inline-field' && (
                            <div
                                className={cn(
                                    'w-full max-w-(--header-search-width) md:mt-(--header-utility-gap-above-search)',
                                    composition.desktopBlock
                                )}
                                data-testid="header-search-desktop">
                                <LocationKeyedSearch />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* The disclosed search panel for brands whose header carries a
                trigger instead of a field (BG). Still mounted only while open —
                Radix unmounts the dialog's content when closed — so "no input in
                the DOM" stays literally true, the property live BG actually has
                (bg-home-d-1280/App.tsx:71-116 contains no <input>). NM never
                renders this.

                IT IS AN OVERLAY, NOT A ROW. What used to sit here was
                `<div className="section-container">` in NORMAL FLOW holding NM's
                `SearchBar`: opening it pushed every band below the header down
                the page. Live BG's panel COVERS — a full-viewport white takeover
                at mobile, a left-edge drawer over a scrim at desktop — and it
                carries BG's own chrome rather than NM's submit button, NM's
                in-input magnifier and NM's recent-searches block. All of that,
                plus the focus trap the "covers rather than unmounts" structure
                forces, lives in bg-search-panel.tsx; the evidence for every
                piece of it is written down there. */}
            {composition.desktopSearch === 'left-panel-trigger' && (
                <BgSearchPanel open={searchPanelOpen} onOpenChange={setSearchPanelOpen} panelId={SEARCH_PANEL_ID} />
            )}

            {/* Mobile search row — full-width, mobile only, and ONLY for brands
                whose search is a persistent field.

                THE GATE IS THE AFFORDANCE, NOT THE BREAKPOINT. This row used to
                render for every brand under `mobileOnly`, which put a
                full-width <input> in BG's mobile header. Live BG has no <input>
                in its header at ANY width: bg-home-m-375 contains exactly two
                <input> elements in the whole file (:577 and :1266, one per the
                capture's duplicated tree) and both are the FOOTER email signup;
                same at 440 (:576, :1264) and at 1280 (:832, :1766). BG's mobile
                search is the trigger in the left group above, and its field
                enters the DOM only once the panel opens — so gating on
                `desktopSearch` makes "absent from the DOM" literally true
                rather than merely hidden.

                NM is untouched by construction: it is the `right-inline-field`
                brand, so this row still renders for NM at exactly the widths it
                did before (live NM DOES stack a search row at mobile —
                micro-harvest-header2 §3 row 3, .exposed-search-box-search-
                container 0,135,375,42). Deleting the row outright would have
                taken NM's mobile search with it. */}
            {composition.desktopSearch === 'right-inline-field' && (
                <div
                    // The FRAME, and it carries a handle only so a brand overlay can
                    // reach it. `.section-container` supplies `px-4` — a 16px inset —
                    // and live NM's at-rest mobile search form insets 10px, so the row
                    // renders 12px narrower than live's at every mobile width. There is
                    // no parent selector and `:has()` is banned, so the frame needs a
                    // handle of its own; the rule lands in theme/overrides/header.css
                    // where the derivation is written down.
                    data-testid="header-search-mobile-frame"
                    className={cn('section-container', composition.mobileOnly)}>
                    {/* ROW GEOMETRY, measured 2026-08-19. Live NM's search row is
                        42px: wrapper `box-content pt-[10px] pr-[10px] pb-0 pl-[10px]`
                        around a 32px field (search-m-390/App.tsx in document order;
                        the 32 re-derived independently as the field's ink box in
                        .reference/live-shots/nm-search-390.png, CSS y 115→147).
                        Ours was 62 — a 46px field under `pb-4`.

                        Every fallback below is the value this row rendered before,
                        so a brand that sets none of these tokens is unmoved. BG
                        never reaches this code at all — the row is gated on
                        `right-inline-field` and BG is `left-panel-trigger` — but the
                        fallbacks make that belt-and-braces rather than load-bearing.

                        The field height is scoped HERE, on the row, not on the brand
                        overlay's `:root`: --text-field-box-height is the shared text
                        field token, and writing 32px at root would resize auth and
                        checkout inputs too. This is the same container-scoping the
                        md+ 28px desktop value uses. */}
                    <div
                        className={cn(
                            '[padding-top:var(--header-search-row-pad-top-mobile,0px)]',
                            '[padding-bottom:var(--header-search-row-pad-bottom-mobile,1rem)]',
                            '[--text-field-box-height:var(--header-search-field-height-mobile,46px)]'
                        )}
                        data-testid="header-search-mobile">
                        <LocationKeyedSearch />
                    </div>
                </div>
            )}

            {/* MOBILE GENDER ROW — [ours-missing], revealed 2026-08-23.

                Live NM's mobile homepage stacks WOMEN | MEN as its OWN full-width
                row between the search input and the cat nav, horizontally CENTRED,
                with WOMEN carrying the active underline. Source: the owner's
                photograph of a real handset,
                .reference/owner-nm-mobile-2026-08-23/dedfeb31-78cd-47ac-a521-
                0f82e3acceb8.jpeg — address bar reads neimanmarcus.com, iOS Safari.
                A photograph is the only route to this: NM serves a DESKTOP response
                to device emulators, so no headless capture can reach the mobile
                arm.

                WHY A SECOND INSTANCE RATHER THAN REPOSITIONING THE DESKTOP ONE.
                The desktop nav lives two levels inside `header-main-row`'s LEFT
                grid cell — a 133px-wide track that also holds the hamburger — and
                it is bottom-anchored there by owner-tuned values
                (--gender-tab-bottom-inset, --gender-tab-offset-y). Live's mobile
                placement is a full-width centred row in a different band
                altogether, so this is a different BOX, not the same box at another
                width. Moving one element between the two would need a JS width
                branch, which this repo forbids in an SSR path — width tiers are
                Tailwind responsive classes throughout, and a width branch here
                would reproduce the hydration mismatch already tracked against this
                page. Two instances, each gated by a responsive class, is the
                mechanism the codebase already uses for exactly this (see the
                mobile search frame directly above).

                The `-mobile` testid suffix is deliberate and load-bearing: it keeps
                `header-gender-nav` and `header-gender-tab-*` resolving to exactly
                ONE node each, so every existing unit test and the e2e page object
                are untouched. See the note on `genderTab` above.

                UNMEASURED — the vertical padding. The photograph settles PRESENCE,
                ORDER and CENTRING, which are scale-invariant; it cannot settle
                pixels, because its CSS width and DPR are not recoverable (it is a
                downscaled handset capture that also includes iOS and Safari chrome
                above the page origin, so absolute Y is contaminated). The two
                tokens below carry the starting values and are the one thing here
                worth an owner eyeball. Type, tracking, underline and gap are NOT
                guesses — they reuse the same --gender-tab-* tokens the desktop arm
                already renders, which is consistent with the photograph at the
                precision it supports. */}
            {composition.genderTabs && mobileGenderTabs && (
                <div
                    data-testid="header-gender-row-mobile"
                    className={cn(
                        'section-container flex justify-center',
                        '[padding-top:var(--gender-row-pad-top-mobile,0.5rem)]',
                        '[padding-bottom:var(--gender-row-pad-bottom-mobile,1rem)]',
                        composition.mobileOnly
                    )}>
                    {genderNavWith('-mobile', 'flex')}
                </div>
            )}

            {/* Nav row — category links centered, desktop only. Fixed 44px band
                (--header-nav-height, border included — owner, 2026-08-16: wrapper
                164 = 90 zone + 30 gap + 44 nav). NO-WRAP guarantee, live
                mechanism (.reference/harvest/micro-harvest-header2.md §5): the
                row is a centered nowrap flex with overflow VISIBLE and constant
                type/spacing at every width — live applies NO compression of any
                kind (no font shrink, no tracking change, no scroll container,
                no item hiding; no breakpoint fires between 1440 and 1100). Once
                the viewport is narrower than the content (~1123px live), the
                centered row simply spills symmetrically past both viewport
                edges. This replaced the interim horizontal-scroll safety valve +
                padding-compression ladder.

                THAT §5 HARVEST IS NEIMAN MARCUS, NOT BG. Its title says "live
                neimanmarcus.com" and its items are New Arrivals…Sale in Futura.
                It is evidence about NM's band only; live BG's behaviour below
                its own content width has never been captured. Note §5 also
                records NM spilling with NO SCROLLBAR (clipped by the window) —
                our BG band instead extends the page's scrollWidth, so we are
                not reproducing even the NM mechanism.

                THE "~960px" FIGURE THIS COMMENT USED TO CARRY WAS WRONG FOR BG
                and was the whole safety margin. Measured 2026-08-20 in-browser
                on BG at 1025 / 1138 / 1280: the list is 1138.11px at EVERY
                width (13 items, 15.444px side padding each — live's own value,
                from bg-home-d-1280), so it never compresses. Result: BG page
                scroll across viewports 1025-1137 inclusive, worst 57px at 1025,
                with the first item ("What's New") painting at x=-56.55 where no
                scroll can reach it. Clean at 1138 and above, and at 1024 and
                below the band is not mounted at all.

                Why it appeared only now: BG's composition gated this band at
                1280 until 2026-08-20, and 1138 fits inside 1280. Moving the
                gate to 1025 did not widen the list — it mounted an
                always-too-wide list 255px lower.

                OUR LIST IS NOT TOO WIDE — DO NOT "FIX" THIS WITH TYPE OR
                PADDING. Live BG's nav ink measures 1106.0px at BOTH 1150
                (owner's search shot, ink 21.5-1127.0) and 1440 (owner's PLP
                shot, ink 166.5-1272.0) — identical, so live applies no
                compression on BG either, and this is now BG-native evidence
                rather than §5's NM inference. Plus live's own 15.444px pads
                that is a ~1136.9px box against our 1138.11: +1.2px, which is
                glyph noise, not a divergence. Both are centred (measured
                centres 574.25 of 575, and 719.25 of 720).

                DO NOT GATE THIS ROW — LIVE DOES NOT. Settled 2026-08-20 by an
                owner capture of live BG's PLP at 1100, i.e. inside the overflow
                band: live renders all thirteen items there and lets the row
                SPILL. Proof it is the same row and not a compressed one: every
                glyph cluster sits exactly 25px left of where it sits at 1150 —
                a constant shift, and precisely half the 50px viewport delta, so
                pure re-centring — except the first, which is truncated at x=0,
                losing 3px off the W of "What's New". That is the predicted
                (1106-1100)/2 spill per side, arriving as clipped ink.

                So live's mount width is at or below 1100, live gates nothing
                near 1138, and OUR SPILL GEOMETRY ALREADY MATCHES LIVE'S: at
                1100 live's box overhangs 18.45px per side and ours overhangs
                19. Gating this row at its intrinsic width would have removed a
                band live actually paints — the opposite of parity.

                NOT A DEFECT — CLOSED 2026-08-20. LIVE SCROLLS TOO. The owner
                ran scrollWidth minus clientWidth on live BG's document element
                at ~1100 and got 19: non-zero, so live lets the page scroll
                horizontally exactly as we do. Live renders the row, spills it,
                AND scrolls. There is nothing here to fix, and §5's NM "no
                scrollbar" reading does not transfer to BG.

                DO NOT OVER-READ THAT INTEGER. Our box overhangs 19.055 per side
                at a 1100 viewport; live's 1136.89 overhangs 18.444. Chrome
                rounds to nearest — calibrated 9/9 against our own sweep, where
                ceiling would have read 20 at 1100 and floor 0 at 1137 — so live
                at EXACTLY 1100 would report 18, and the owner's 19 means his
                window was nearer 1098-1099. The read proves the SIGN (live
                scrolls, 19 != 0), not sub-pixel parity. The width comparison
                rests on the ink measurement above, not on this integer.

                METHOD, because this class of question recurs: a screenshot
                cannot answer "is there a scrollbar". The positive control that
                voided the visual method is that both owner captures certainly
                scroll VERTICALLY, yet neither shows a vertical scrollbar — so
                the instrument cannot see scrollbars at all, and the absence of
                a horizontal one was never evidence. That is why this took a
                console read on live rather than a fourth capture.

                SCOPE — do not generalise one width into a claim about the band.
                Scrolling is measured at ~1100 only; the row's constant width is
                measured at 1100/1150/1440; the row's absence is measured at
                1024. Live's exact mount width inside (1024, 1100] is NOT
                pinned, so "live's band starts at 1025 like ours" is inferred
                from the composition boundary, not measured. Our band is
                1025-1137, worst 57px at 1025; live's predicted band is
                1025-1136, worst ~56px, one pixel narrower because its box is
                1.22px narrower. Same shape, not identical numbers. */}
            {/* NM SPILLS TOO — SAME ANSWER, INDEPENDENTLY MEASURED, 2026-08-24.
                Recorded here beside the BG note because the BG evidence above
                is BG-native and does NOT transfer: different template,
                different label set, different type tier. The NM question was
                opened separately and closed separately.

                OUR NM cat-nav overflows 1076-1110. The list box is 1111.36 at
                that tier's 20px gap, the tier gate below hands off at 1075.98,
                and the row does not fit until 1111 by scrollWidth or 1112 by
                the list box. Sweep, noise floor and the corrected boundary are
                in theme/overrides/navigation.css; the number that used to be
                recorded there was 1076 and is stale.

                LIVE NM SPILLS ACROSS ALL OF IT. Live's cat-nav at its own 18px
                tier is a shrink-to-fit centred row of CONSTANT intrinsic width
                — ink span 301.00 to 1428.50 = 1128.00, gaps mean 29.04 SD 0.75,
                margins 301 / 299 — read by column ink profile off the owner's
                two DevTools-closed 1728 captures and IDENTICAL on both, all
                thirteen clusters at the same CSS position. Ours over the same
                instrument at the same pinned 1728 is 301.00 to 1427.00 =
                1127.00, gaps 29.17 SD 0.80. One pixel apart on the span, and
                every cluster within 1px — which is also the positive control
                proving we render live's actual face and not a fallback.

                A 1128-wide nowrap row, centred, with no overflow declaration
                anywhere on live's nav chain, cannot do anything but spill below
                1128. So live's spill band is roughly 1025-1128 and strictly
                CONTAINS ours, and ours is the gentler one: at 1100 we overhang
                5.67 / 5.69 per side against live's 14.0. Our render at 1100
                carries the same signature as BG's — all thirteen labels, New
                Arrivals shaved at x=0, Sale losing 3px at the right edge.

                DO NOT GATE THE NM ROW IN 1076-1110 EITHER. Same conclusion and
                same reason as BG directly above: gating at the intrinsic width
                removes a band live actually paints.

                SCOPE, stated because the BG note's own scope paragraph is the
                right discipline: live's gap between 1076 and 1279 is UNMEASURED
                (22px is measured at 1280/1440/1728 only). The conclusion is
                immune to it — at 22 live spills 1025-1135, at 20 live spills
                1025-1110, and there is no value under which live is clean where
                we spill. What is NOT pinned is live's exact first clean width,
                only that it is at or above ours. */}
            {/* THE NAV BAND'S HAIRLINE IS ITS OWN COLOUR, and establishing WHICH
                NODE live paints was the whole of this row — the shape that
                produced the defect is right-colour-on-wrong-element, so doing
                the inverse would have been the same error with the sign
                flipped. What was NOT true is that the value simply wanted
                moving off --header-main-border-color: that token is CORRECT
                where it is, and moving it would have broken BG at 375-1024.

                THE DISCRIMINATING READ. In BG's desktop capture the entire
                header region contains exactly ONE `border-b`, and it is this
                band — the div that directly wraps the <nav> holding the 13
                silo links:

                  bg-home-d-1280/App.tsx:117 (and :1051, the page's second
                  emission of the same node)
                  <div className="border-b box-content flex justify-center
                                  relative bg-white pt-0 pr-0 pb-[23px] pl-0
                                  z-[1003]"
                       style={{"borderBottomColor":"rgb(225, 225, 235)"}}>
                    <nav …><ul …>  what's new · shop by · designers · … · sale

                Counted over lines 1-120 with `div` as the positive control (84
                divs, 1 `border-b`), so the wordmark/utility band that closes
                immediately above it carries NO border at any width — which is
                why --header-main-border-width is already 0 for BG at this tier
                and must stay 0. Same node, same colour, same `pb-[23px]` at
                1440 (bg-search-d-1440:118, bg-pdp-d-1440:123).

                AND THE OTHER TIER IS THE REASON THIS IS AN ADDITION, NOT A
                MOVE. At 375/440/768/1024 live BG serves its MOBILE header —
                one band, no nav row at all — and the SAME hairline colour sits
                on that band instead (bg-home-m-375:71, bg-home-d-1024:71,
                `h-[69px]` in place of `pb-[23px]`; `pr-[15.444px]` returns 0
                hits at 768 and 1024 and 26 at 1280+, which is how the two
                tiers are told apart). --header-main-border-color already
                carries it there and already paints. So both bands are live's,
                at different widths, and each needs its own token.

                `var(--token, <neutral>)` rather than a shared declaration: NM's
                nav-row hairline colour is UNMEASURED — the 225,225,225 in the
                comment on the <header> above is NM's whole-assembly rule at
                mobile, a different box on a different element — so the
                fallback keeps today's --header-border paint for NM and makes
                no shared claim about a value nobody read. */}
            <div
                className={cn(
                    'border-b [border-bottom-color:var(--header-nav-border-color,var(--header-border))] h-(--header-nav-height)',
                    composition.desktopBlock
                )}
                data-testid="header-nav-row">
                {/* NAV-ROW GUTTER IS ITS OWN TOKEN, not `.section-container`.
                    That utility is `px-4 sm:px-8 lg:px-16` (theme/base.css) and
                    frames 10+ components on both brands, so its 64px at lg+ is a
                    PAGE gutter that this row was inheriting rather than a value
                    anyone measured for it. Live NM's nav declares its own, and it
                    is a LADDER — quoted from the captures, each of which emits the
                    page twice, so these are one occurrence apiece and they agree
                    across two independent surfaces (home and search):

                      768   pt-[3px] pr-[13px] pl-[13px]
                      1024  pt-[9px] pr-[13px] pl-[13px]
                      1280  pt-[9px] pr-[27px] pl-[27px]
                      1440  pt-[9px] pr-[27px] pl-[27px]
                      1920  pt-[9px] pr-[27px] pl-[27px]

                    So the gutter steps at 1280 and the TOP PAD steps at 1024 —
                    two different boundaries inside one row. Only the gutter is
                    applied here; the 3px/9px top pad is measured and unapplied,
                    and is recorded in the change report rather than folded in
                    silently.

                    BG IS PINNED AT TODAY'S 64px AND THAT IS NOT A MEASUREMENT.
                    Live BG's nav row is a different element entirely — `flex
                    justify-center relative w-full` with NO padding classes
                    (bg-home-d-1280, bg-search-d-1440) — and it does not exist at
                    all below 1025 (bg-home-d-1024 and bg-search-d-1024 contain
                    zero `<nav>` elements against control div counts of 1044 and
                    3888). Applying NM's 13/27 ladder to BG would be the
                    measured-one-brand-assumed-the-other error; BG's own value is
                    a separate row on a surface this change does not own.

                    MEASURED RESULT AT 1024, brand nm: first link ink x 80.7 ->
                    36.0 against live's 34.8, and the link ink span 873.2 -> 966.6
                    against live's 953.0. The right edge overshoots by ~15px
                    because live DISTRIBUTES the row differently — see the change
                    report; that half lives in components/ui/navigation-menu.tsx,
                    which this change does not own. */}
                {/* Same frame cap as the band above, from the same token, so
                    the two cannot drift apart — the owner's actual question was
                    "why isn't the whole page getting the same frame from the
                    same place?". Neutral 96rem restates `max-w-screen-2xl`. */}
                <div
                    className={cn(
                        'mx-auto px-(--header-nav-row-pad-x) h-full',
                        '[max-width:var(--header-band-max-width,96rem)]'
                    )}>
                    {/* VERTICAL RHYTHM OF THE NAV TEXT.
                        Owner, 2026-08-21: "the cat nav words sit too low, they
                        should be higher a bit closer to the logo with more
                        spacing below to the gray header border."
                        Measured paint-to-paint at 1280: our gap from the
                        wordmark's ink bottom to the nav ink top is 38.5 against
                        live's 29.5, and the ink band is 9.5px tall in both, so
                        only the placement is off. This row is a fixed-height
                        box with a vertically centred line, so padding-bottom of
                        2x the correction lifts the line by exactly half of it
                        and leaves the grey border — the thing the "spacing
                        below" is measured against — where it is. Derivation and
                        both captures are in theme/tokens/brand-bg/header.css,
                        "Category-nav row: vertical rhythm".
                        Neutral 0px = what NM renders today, so this is inert
                        for every brand that does not set it. */}
                    {/* Arbitrary PROPERTY, not `pb-(--token)`: Tailwind's
                        spacing shorthand has no fallback form, and the neutral
                        has to live here because theme/tokens/header.css is not
                        this change's to write. */}
                    {/* `--header-nav-row-pad-top` is the mirror of the
                        pad-bottom above and works by the same arithmetic: the
                        row is a fixed-height box with a vertically centred
                        line, so N px of padding on one side moves the line N/2
                        toward the other. Padding-top LOWERS the text and leaves
                        the row's own height — and therefore the grey border
                        under it — exactly where it is, which is what makes it
                        safe against a header height the owner has signed off.
                        NM's 5px and the ink scan behind it are on
                        --header-nav-row-pad-top in tokens/brand-nm/header.css.
                        Neutral 0px = what every brand renders today. */}
                    <div className="flex h-full items-center justify-center [padding-top:var(--header-nav-row-pad-top,0px)] [padding-bottom:var(--header-nav-row-pad-bottom,0px)]">
                        {children}
                    </div>
                </div>
            </div>

            <UITarget targetId="sfcc.header.bnpl.banner" />
        </header>
    );
}
